import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Download, ShieldAlert, CornerDownRight, ChevronDown, ChevronUp, Circle } from 'lucide-react';
import { STATUS } from '@/pages/OperatingModesPage';

const ACTOR_COLOR = {
  engine: '#1B4A9A', worker: '#5C6B7A', underwriter: '#1D9E75',
  admin: '#6C4BB5', broker: '#D96000', insured: '#B07407', external: '#0E7490',
};

export default function FlowMapPage() {
  const api = useApi();
  const [map, setMap] = useState(null);
  const [lens, setLens] = useState('workflows');
  const [wfId, setWfId] = useState('new_business');
  const [highlight, setHighlight] = useState('');
  const [focusStage, setFocusStage] = useState('');

  useEffect(() => {
    api.get('/workflow-map').then(r => setMap(r.data)).catch(err => {
      console.error('flow map error:', err?.message);
      toast.error('Could not load the flow map');
    });
  }, [api]);

  const download = async () => {
    try {
      const res = await api.get('/workflow-map/markdown', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'workflow_map.md'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Flow map document downloaded');
    } catch { toast.error('Download failed'); }
  };

  const jumpToStage = (workflow, stage) => {
    setLens('workflows'); setWfId(workflow); setFocusStage(stage); setHighlight('');
  };

  if (!map) return <Skeleton className="h-96 rounded-sm" data-testid="flow-map-loading" />;

  const s = map.summary;
  const wf = map.workflows.find(w => w.id === wfId) || map.workflows[0];

  return (
    <div className="space-y-4" data-testid="flow-map-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm max-w-2xl" style={{ color: '#5C6B7A' }}>
          Every flow the platform runs, in the order it runs it. Each stage names who runs it, what
          it can refuse at, what happens when it refuses, and the function that carries it — read
          off the code, not written beside it.
        </p>
        <button onClick={download} data-testid="flow-map-download"
          className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-sm border transition-colors hover:bg-[#F5F7FA]"
          style={{ color: '#1B4A9A', borderColor: 'rgba(27,74,154,0.3)' }}>
          <Download className="w-3.5 h-3.5" /> Download the document
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="flow-map-summary">
        {[
          { l: 'Workflows', v: s.workflows }, { l: 'Stages', v: s.stages }, { l: 'Gates', v: s.gates },
          { l: 'Classes', v: `${s.classes_quotable}/${s.classes}` }, { l: 'Modes wired', v: s.wired },
          { l: 'Partial', v: s.partial }, { l: 'Described', v: s.described },
        ].map(k => (
          <Card key={k.l} className="p-3 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
            <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#5C6B7A' }}>{k.l}</p>
            <p className="text-lg font-semibold mt-0.5" style={{ color: '#0B1E3D', fontFamily: 'Georgia, serif' }}>{k.v}</p>
          </Card>
        ))}
      </div>

      <div className="flex gap-2 border-b pb-2" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        {[{ id: 'workflows', label: `Workflows · ${s.workflows}` },
          { id: 'classes', label: `Classes · ${s.classes}` },
          { id: 'modes', label: `Modes · ${s.modes}` },
          { id: 'findings', label: `Findings · ${s.findings}` }].map(t => (
          <button key={t.id} onClick={() => setLens(t.id)} data-testid={`lens-${t.id}`}
            className="px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider transition-colors"
            style={lens === t.id ? { background: '#1B4A9A', color: '#FFF' } : { color: '#5C6B7A', background: 'rgba(136,150,168,0.1)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {lens === 'workflows' && (
        <div className="grid lg:grid-cols-[260px_1fr] gap-4">
          <div className="space-y-1.5">
            {map.workflows.map(w => (
              <button key={w.id} onClick={() => { setWfId(w.id); setFocusStage(''); }} data-testid={`wf-select-${w.id}`}
                className="w-full text-left p-3 rounded-sm border transition-colors"
                style={w.id === wfId
                  ? { borderColor: '#1B4A9A', background: 'rgba(27,74,154,0.06)' }
                  : { borderColor: 'rgba(136,150,168,0.2)', background: '#FFF' }}>
                <p className="text-xs font-bold" style={{ color: '#0B1E3D' }}>{w.name}</p>
                <p className="text-[10px] mt-0.5" style={{ color: '#5C6B7A' }}>{w.stage_count} stages</p>
              </button>
            ))}
            <div className="p-3 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.2)', background: '#FFF' }}>
              <p className="text-[9px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#5C6B7A' }}>Highlight the stages a mode would change</p>
              <select value={highlight} onChange={e => setHighlight(e.target.value)} data-testid="mode-highlight-select"
                className="w-full text-[11px] p-1.5 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0B1E3D' }}>
                <option value="">No mode selected</option>
                {map.mode_categories.flatMap(c => c.modes).map(m => (
                  <option key={m.id} value={m.id}>{`${m.name} — ${m.status}`}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
              <h2 className="text-base font-semibold" style={{ color: '#0B1E3D', fontFamily: 'Georgia, serif' }}>{wf.name}</h2>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: '#5C6B7A' }}>{wf.purpose}</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#5C6B7A' }}>Trigger</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#0B1E3D' }}>{wf.trigger}</p>
                </div>
                <div className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#5C6B7A' }}>Entry point</p>
                  <p className="text-[11px] mt-0.5 font-mono" style={{ color: '#0B1E3D' }}>{wf.code_ref}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {wf.exits.map(x => (
                  <span key={x.state} className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" title={x.meaning}
                    style={{ background: 'rgba(27,74,154,0.08)', color: '#1B4A9A' }}>{x.state}</span>
                ))}
              </div>
            </Card>

            <div className="space-y-1.5">
              {wf.stages.map(st => (
                <StageCard key={st.id} stage={st} highlight={highlight} focus={focusStage === st.id} />
              ))}
            </div>

            {wf.side_effects?.length > 0 && (
              <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
                <p className="text-[9px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#5C6B7A' }}>Running alongside</p>
                {wf.side_effects.map((x, i) => (
                  <p key={i} className="text-[11px] flex gap-1.5 leading-relaxed" style={{ color: '#5C6B7A' }}>
                    <Circle className="w-2 h-2 mt-1.5 shrink-0" style={{ color: '#1D9E75', fill: '#1D9E75' }} />{x}
                  </p>
                ))}
              </Card>
            )}
          </div>
        </div>
      )}

      {lens === 'classes' && <ClassLens classes={map.classes} onOpenFlow={() => { setLens('workflows'); setWfId('new_business'); }} />}
      {lens === 'modes' && <ModeLens categories={map.mode_categories} onJump={jumpToStage} />}
      {lens === 'findings' && <FindingsLens findings={map.findings} waves={map.enforcement?.waves || []} />}
    </div>
  );
}

const KIND = {
  honesty: { label: 'HONESTY', fg: '#1B4A9A', bg: 'rgba(27,74,154,0.1)' },
  leak: { label: 'LEAK', fg: '#E24B4A', bg: 'rgba(226,75,74,0.1)' },
  gap: { label: 'GAP', fg: '#B07407', bg: 'rgba(232,160,32,0.14)' },
  ruling: { label: "OWNER'S RULING", fg: '#1D9E75', bg: 'rgba(29,158,117,0.12)' },
  open: { label: 'OPEN', fg: '#6C4BB5', bg: 'rgba(108,75,181,0.1)' },
};

function FindingsLens({ findings, waves }) {
  return (
    <div className="space-y-3" data-testid="findings-lens">
      <p className="text-sm max-w-3xl" style={{ color: '#5C6B7A' }}>
        What walking every flow exposed — each with the evidence that produced it, what it means,
        and the fix. Nothing here is inferred from a document; it is what the code does.
      </p>
      {waves.length > 0 && (
        <Card className="p-3 rounded-sm border bg-white" style={{ borderColor: 'rgba(27,74,154,0.25)' }}>
          <p className="text-[9px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#1B4A9A' }}>The plan, in waves</p>
          {waves.map(w => (
            <p key={w.wave} className="text-[11px] leading-relaxed" style={{ color: '#5C6B7A' }}>
              <span className="font-bold" style={{ color: '#0B1E3D' }}>W{w.wave} · {w.name}</span> — {w.basis}
            </p>
          ))}
        </Card>
      )}
      {findings.map(f => {
        const k = KIND[f.kind] || KIND.open;
        return (
          <Card key={f.id} className="p-4 rounded-sm border bg-white space-y-2" style={{ borderColor: 'rgba(136,150,168,0.2)' }}
            data-testid={`finding-${f.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider" style={{ background: k.bg, color: k.fg }}>{k.label}</span>
              <span className="text-[13px] font-semibold" style={{ color: '#0B1E3D' }}>{f.title}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#5C6B7A' }}>{f.status}</span>
              {f.wave && <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(27,74,154,0.08)', color: '#1B4A9A' }}>wave {f.wave}</span>}
            </div>
            <p className="text-[10px] font-mono" style={{ color: '#8896A8' }}>{f.where}</p>
            <div className="grid md:grid-cols-3 gap-2">
              {[{ l: 'Evidence', v: f.evidence, c: '#5C6B7A' }, { l: 'What it means', v: f.means, c: '#0B1E3D' }, { l: 'Fix', v: f.fix, c: '#1B4A9A' }].map(b => (
                <div key={b.l} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                  <p className="text-[9px] uppercase tracking-wider font-bold mb-0.5" style={{ color: b.c }}>{b.l}</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: '#0B1E3D' }}>{b.v}</p>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function StageCard({ stage, highlight, focus }) {
  const [open, setOpen] = useState(false);
  const dim = highlight && !(stage.modes || []).includes(highlight);
  const hit = highlight && (stage.modes || []).includes(highlight);
  const color = ACTOR_COLOR[stage.actor] || '#5C6B7A';
  return (
    <Card data-testid={`stage-card-${stage.id}`}
      className="rounded-sm border bg-white overflow-hidden transition-opacity"
      style={{
        borderColor: focus ? '#1B4A9A' : hit ? '#1D9E75' : 'rgba(136,150,168,0.2)',
        opacity: dim ? 0.38 : 1,
        boxShadow: focus ? '0 0 0 2px rgba(27,74,154,0.15)' : 'none',
      }}>
      <button onClick={() => setOpen(o => !o)} data-testid={`stage-expand-${stage.id}`}
        className="w-full p-3 flex items-start gap-3 text-left hover:bg-[#F5F7FA] transition-colors">
        <span className="text-[10px] font-mono font-bold w-6 h-6 shrink-0 rounded-sm flex items-center justify-center"
          style={{ background: color + '15', color }}>{stage.seq}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold" style={{ color: '#0B1E3D' }}>{stage.name}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider"
              style={{ background: color + '15', color }}>{stage.actor}</span>
            {stage.within && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(136,150,168,0.1)', color: '#5C6B7A' }}>
                inside {stage.within}
              </span>
            )}
            {stage.gate && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-semibold flex items-center gap-1"
                style={{ background: 'rgba(226,75,74,0.08)', color: '#E24B4A' }}>
                <ShieldAlert className="w-2.5 h-2.5" /> gate
              </span>
            )}
          </div>
          <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#5C6B7A' }}>{stage.does}</p>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 shrink-0" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: '#5C6B7A' }} />}
      </button>
      {open && (
        <div className="px-3 pb-3 pl-12 space-y-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
          {stage.gate && (
            <div className="p-2 rounded-sm mt-2" style={{ background: 'rgba(226,75,74,0.04)', border: '1px solid rgba(226,75,74,0.12)' }}>
              <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#E24B4A' }}>Refuses at</p>
              <p className="text-[11px] mt-0.5 font-mono" style={{ color: '#0B1E3D' }}>{stage.gate}</p>
            </div>
          )}
          <div className="p-2 rounded-sm" style={{ background: 'rgba(29,158,117,0.04)', border: '1px solid rgba(29,158,117,0.12)' }}>
            <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#1D9E75' }}>Fails closed</p>
            <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: '#0B1E3D' }}>{stage.fail_closed}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#1B4A9A' }}
              data-testid={`stage-code-${stage.id}`}>{stage.code_ref}</span>
            {(stage.modes || []).map(m => (
              <span key={m} className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono"
                style={{ background: m === highlight ? 'rgba(29,158,117,0.15)' : 'rgba(136,150,168,0.1)', color: m === highlight ? '#1D9E75' : '#5C6B7A' }}>{m}</span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function ClassLens({ classes, onOpenFlow }) {
  const [open, setOpen] = useState(null);
  const blocked = useMemo(() => classes.filter(c => !c.quotable), [classes]);
  return (
    <div className="space-y-3" data-testid="class-lens">
      <Card className="p-3 rounded-sm border bg-white" style={{ borderColor: blocked.length ? 'rgba(226,75,74,0.3)' : 'rgba(29,158,117,0.3)' }}>
        <p className="text-xs" style={{ color: '#0B1E3D' }}>
          {classes.length - blocked.length} of {classes.length} classes may be quoted right now.
          {blocked.length > 0 && <> Blocked: <strong>{blocked.map(b => b.name).join(', ')}</strong> — read live from the class gate, not typed into this page.</>}
        </p>
      </Card>
      <Card className="rounded-sm border bg-white overflow-x-auto" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: '#0B1E3D' }}>
              {['Class', 'Phase', 'Quotable', 'Wording', 'Rating inputs (any of)', 'Refer triggers', 'Fields', ''].map(h => (
                <th key={h} className="text-left p-2 text-white font-semibold text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classes.map((c, i) => (
              <React.Fragment key={c.id}>
                <tr style={i % 2 === 0 ? { background: '#F5F7FA' } : {}} data-testid={`class-row-${c.id}`}>
                  <td className="p-2 font-semibold" style={{ color: '#0B1E3D' }}>{c.name}
                    <span className="block text-[9px] font-mono font-normal" style={{ color: '#8896A8' }}>{c.id}</span>
                  </td>
                  <td className="p-2" style={{ color: '#5C6B7A' }}>{c.phase || '—'}</td>
                  <td className="p-2">
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold"
                      style={c.quotable ? { background: 'rgba(29,158,117,0.12)', color: '#1D9E75' } : { background: 'rgba(226,75,74,0.12)', color: '#E24B4A' }}>
                      {c.quotable ? 'YES' : 'BLOCKED'}
                    </span>
                  </td>
                  <td className="p-2 font-mono" style={{ color: c.wording_version ? '#0B1E3D' : '#E24B4A' }}>{c.wording_version || 'none'}</td>
                  <td className="p-2" style={{ color: '#5C6B7A' }}>{(c.rating_inputs || []).join(', ') || '—'}</td>
                  <td className="p-2" style={{ color: '#5C6B7A' }}>{c.refer_triggers}</td>
                  <td className="p-2" style={{ color: '#5C6B7A' }}>{c.declared_fields}</td>
                  <td className="p-2">
                    <button onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`class-expand-${c.id}`}
                      className="text-[10px] font-semibold" style={{ color: '#1B4A9A' }}>
                      {open === c.id ? 'Hide' : 'Detail'}
                    </button>
                  </td>
                </tr>
                {open === c.id && (
                  <tr>
                    <td colSpan={8} className="p-3" style={{ background: '#FFF', borderTop: '1px solid rgba(136,150,168,0.15)' }}>
                      <p className="text-[11px] leading-relaxed" style={{ color: '#5C6B7A' }}>{c.description}</p>
                      {c.blockers?.length > 0 && (
                        <div className="mt-2 p-2 rounded-sm" style={{ background: 'rgba(226,75,74,0.05)', border: '1px solid rgba(226,75,74,0.15)' }}>
                          {c.blockers.map(b => (
                            <p key={b.code} className="text-[11px]" style={{ color: '#0B1E3D' }}>
                              <span className="font-mono font-bold" style={{ color: '#E24B4A' }}>{b.code}</span> — {b.detail}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#5C6B7A' }}>
                          rate table: {c.rate_table ? 'yes' : 'no'}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#5C6B7A' }}>
                          sections: {c.sections ? 'yes' : 'no'}
                        </span>
                        <button onClick={onOpenFlow} data-testid={`class-flow-${c.id}`}
                          className="text-[10px] font-semibold flex items-center gap-1" style={{ color: '#1B4A9A' }}>
                          <CornerDownRight className="w-3 h-3" /> runs the New business flow
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ModeLens({ categories, onJump }) {
  return (
    <div className="space-y-4" data-testid="mode-lens">
      {categories.map(cat => (
        <div key={cat.id} className="space-y-1.5">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>{cat.name}</h2>
            <span className="text-[11px]" style={{ color: '#8896A8' }}>{cat.description}</span>
          </div>
          {cat.modes.map(m => {
            const st = STATUS[m.status] || STATUS.described;
            return (
              <Card key={m.id} className="p-3 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid={`mode-row-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold" style={{ color: '#0B1E3D' }}>{m.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(27,74,154,0.08)', color: '#1B4A9A' }}>wave {m.wave}</span>
                </div>
                <p className="text-[11px] mt-1 font-mono" style={{ color: '#8896A8' }}>↳ {m.control_surface}</p>
                <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#5C6B7A' }}>{m.today}</p>
                {m.activate_at && (
                  <p className="text-[10px] mt-1" style={{ color: '#1B4A9A' }}>Driven from: {m.activate_at}</p>
                )}
                {m.stages?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-[9px] uppercase tracking-wider font-bold mr-1" style={{ color: '#5C6B7A' }}>Stages it changes</span>
                    {m.stages.map(s => (
                      <button key={`${s.workflow}-${s.stage}`} onClick={() => onJump(s.workflow, s.stage)}
                        data-testid={`stage-jump-${m.id}-${s.stage}`}
                        className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono transition-colors hover:brightness-95"
                        style={{ background: 'rgba(27,74,154,0.08)', color: '#1B4A9A' }}>
                        {s.workflow}/{s.stage}
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ))}
    </div>
  );
}
